<template>
  <div v-if="viewer" class="text" v-html="value"></div>
  <div v-else
    class="prosemirror-editor no-drag"
    :class="{ 'format-painter': textFormatPainter, 'alternate-color': alternateColor }"
    ref="editorViewRef"
    @mousedown="$event => emit('mousedown', $event)"
  ></div>
</template>

<script lang="ts" setup>
import { TextSelection } from "prosemirror-state";

import {computed, nextTick, onMounted, onUnmounted, ref, watch} from 'vue'
import { debounce } from 'lodash'
import { storeToRefs } from 'pinia'
import { useMainStore, useWidgetStore, useSlidesStore } from '@/store'
import { EditorView } from 'prosemirror-view'
import { toggleMark, wrapIn } from 'prosemirror-commands'
import { initProsemirrorEditor, createDocument } from '@/utils/prosemirror'
import { findNodesWithSameMark, getTextAttrs, autoSelectAll, addMark, markActive, getFontsize, getBold, getItalic, getUnderline, getColor, getListState } from '@/utils/prosemirror/utils'
import emitter, { EmitterEvents, RichTextAction, RichTextCommand } from '@/utils/emitter'
import { alignmentCommand } from '@/utils/prosemirror/commands/setTextAlign'
import { indentCommand } from '@/utils/prosemirror/commands/setTextIndent'
import { toggleList } from '@/utils/prosemirror/commands/toggleList'
import {PPTDragDropElement, PPTSlideLayoutElement} from "@/types/slides";


const props = defineProps({
  elementId: {
    type: String,
    required: false,
  },
  alternateColor: {
    type: String,
  },
  defaultColor: {
    type: String,
    required: true,
  },
  defaultFontName: {
    type: String,
    required: true,
  },
  editable: {
    type: Boolean,
    default: false,
  },
  value: {
    type: String,
    required: true,
  },
  autoFocus: {
    type: Boolean,
    default: false,
  },
  ignoreEvents: {
    type: Boolean,
    default: false,
  },
  viewer: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits<{
  (event: 'update', payload: string): void
  (event: 'focus'): void
  (event: 'blur'): void
  (event: 'mousedown', payload: MouseEvent): void
}>()

const mainStore = useMainStore()
const { handleElementId, textFormatPainter } = storeToRefs(mainStore)

const { activeId, textSelected, selectionFontSize, alignment, selectionBold, selectionItalic, selectionUnderline, selectionColor, selectionList } = storeToRefs(useWidgetStore())

const editorViewRef = ref<HTMLElement>()
let editorView: EditorView

const alternateColor = computed(() => props.alternateColor ? `${props.alternateColor} !important` : null)

// Various interactive event monitoring of rich text:
// Cancel the global shortcut key event when focusing
// Synchronize data to vuex when entering text
// Synchronize the rich text state to the toolbar when the mouse and keyboard are clicked
const handleInput = debounce(function() {
  if (!selectedWidget.value.prototypeTile) emit('update', editorView.dom.innerHTML)
}, 300, { trailing: true })

const handleFocus = () => {
  mainStore.setDisableHotkeysState(true)
  emit('focus')
}

const handleBlur = () => {
  mainStore.setDisableHotkeysState(false)
  emit('blur')
}

const focusSelection = () => {
  unhighlight()
  if (selectFrom.value > -1) {
    selectShowing.value = false
    TextSelection.create(editorView.state.doc, selectFrom.value, selectTo.value);
    selectFrom.value = -1
  }
}

const showSelection = () => {
  const {from, to} = editorView.state.selection
  if (from !== to) {

    if (!selectShowing.value) {
      selectShowing.value = true
      toggleMark(editorView.state.schema.marks.span)(editorView.state, editorView.dispatch)

      selectFrom.value = from
      selectTo.value = to
    }
  }
}

const allTextSelected = () => {
  const { from, to } = editorView.state.selection;
  const docSize = editorView.state.doc.content.size - 1;
  return from === 1 && (to === docSize || to === 1);
}

const unselect = () => {
  selectShowing.value = false
  selectFrom.value = -1
  unhighlight()
}

const unhighlight = () => {
  editorView.state.doc.nodesBetween(0, editorView.state.doc.content.size, (node) => {
    if (editorView.state.schema.marks.span.isInSet(node.marks)) {
      try {
        editorView.dispatch(editorView.state.tr.removeMark(0, editorView.state.doc.content.size, editorView.state.schema.marks.span))
      }
      catch (e) {}
    }
  })
}

const handleClick = debounce(function() {
  const attrs = getTextAttrs(editorView, {
    color: props.defaultColor,
    // fontname: props.defaultFontName,
  })
  mainStore.setRichtextAttrs(attrs)
}, 30, { trailing: true })

const handleKeydown = () => {
  handleInput()
  handleClick()
}

const handleSelectionChange = () => {
  const selection = window.getSelection()

  if (editorViewRef.value?.contains(document.activeElement)) {
    onTextSelected(selection?.anchorNode, selection?.type)
  }

  if(!document.activeElement?.classList.contains('preserve-select-text') &&
      !document.activeElement?.classList.contains('ProseMirror')) {
    // case 1 - moved to a not proemirror related thing - unselect
    textSelected.value = false
    unselect()
  }
  else if (document.activeElement?.classList.contains('ProseMirror')) {
    // case 2 - moved to prosemirror, focus
    focusSelection()
  }
  else {
    // case 3 - moved to preserve-text, show selection
    showSelection()
  }
}

// Synchronize rich text content to DOM
const textContent = computed(() => props.value)
watch(textContent, () => {
  if (!editorView) return
  if (editorView.hasFocus()) return

  const { doc, tr } = editorView.state
  editorView.dispatch(tr.replaceRangeWith(0, doc.content.size, createDocument(textContent.value)))
})

// Turn on/off the editor's edit mode
watch(() => props.editable, () => {
  editorView.setProps({ editable: () => props.editable })
})

// Expose the focus method
const focus = () => editorView.focus()
defineExpose({ focus })

const selectFrom = ref(-1)
const selectTo = ref(-1)
const selectShowing = ref(false)

const widgetStore = useWidgetStore()
const { selectedWidget, selectedSubsectionRowId, selectedSubsectionId, selectedSectionRowId, selectedSectionId, selectedDragDropId } = storeToRefs(widgetStore)
const slidesStore = useSlidesStore()

const getSelectedDragDrop = () => {
  if (selectedDragDropId.value) {
    for (const dragdrop of slidesStore.getElementsByType('dragdrop')) {
      if (dragdrop.id === selectedDragDropId.value) {
        return <PPTDragDropElement>dragdrop
      }
    }
  }

  return <PPTDragDropElement>slidesStore.getElementsByType('dragdrop')[0]
}

const findSubsection = computed(() => {
  const dragDropElement = getSelectedDragDrop()
  const row = dragDropElement.layout.find((row) => row.id === selectedSubsectionRowId.value)
  if (row) {
    return row.children.find((col) => col.id === selectedSubsectionId.value)
  }
})

const getSlideLayoutElement = () => {
  return slidesStore
      .getElementsByType('slidelayout')
      .find((element) =>
          element.layout.children.some((row) =>
              row.children.some((section) => section.id === selectedSectionId.value)
          )
      )
}

const findSection = computed(() => {
  const slideLayoutElement: PPTSlideLayoutElement = <PPTSlideLayoutElement>getSlideLayoutElement()
  if (slideLayoutElement) {
    const row = slideLayoutElement.layout.children.find((row) => row.id == selectedSectionRowId.value)
    if (row) {
      return row.children.find((col) => col.id === selectedSectionId.value)
    }
  }
  return {}
})

const savePrototypeTileStyle = (item) => {
  // Map camelcase to css styles
  const commandMap = {
    fontsize: 'fontSize',
    bold: 'fontWeight',
    em: 'fontStyle',
    underline: 'textDecoration'
  }

  const styleCommand = commandMap[item.command] || item.command
  selectedWidget.value.style[styleCommand] = item.value

  const subsection = findSubsection.value
  const section = findSection.value

  if (subsection.usePrototypeTile) subsection.prototypeTileStyle =  selectedWidget.value.style
  if (section.usePrototypeTile) section.prototypeTileStyle =  selectedWidget.value.style
}

// Execute rich text commands (can be one or more)
// Before executing some commands, judge whether the current selection area is empty, and if the selection area is empty, perform a full selection operation
const execCommand = ({ target, action }: RichTextCommand) => {

  if (props.ignoreEvents) return
  if (!props.elementId) return

  if (!target && activeId.value !== props.elementId) return
  if (target && target !== props.elementId) return

  const actions = ('command' in action) ? [action] : action
  let inFocus = true

  for (const item of actions) {

    if (item.command === 'focus') {
      focusSelection()
    }

    if (item.command === 'getFontSize') {
      inFocus = false
      const fontSize = getFontsize(editorView);
      fontSize && (selectionFontSize.value = fontSize)
    }

    if (item.command === 'showSelection') {
      showSelection()

      const fontSize = getFontsize(editorView);
      const align = getAlignment(editorView);
      const bold = getBold(editorView)
      const italic = getItalic(editorView)
      const underline = getUnderline(editorView)
      const color = getColor(editorView)
      const bulletList = getListState(editorView)

      fontSize && (selectionFontSize.value = fontSize)
      color && (selectionColor.value = color)
      selectionBold.value = bold
      selectionItalic.value = italic
      selectionUnderline.value = underline
      align && (alignment.value = align)
      selectionList.value = bulletList

    }

    if (item.command === 'fontname' && item.value) {
      const mark = editorView.state.schema.marks.fontname.create({ fontname: item.value })
      if (autoSelectAll(editorView)) {
        showSelection()
      }
      addMark(editorView, mark)
    }
    else if (item.command === 'fontsize' && item.value) {
      inFocus = false
      if (selectedWidget.value.prototypeTile) {
        const allText = allTextSelected()
        if (allText) {
          savePrototypeTileStyle(item)
        }
      } else {
        if (selectFrom.value > -1) {
          // and restore the selection
          const newSelection = TextSelection.create(editorView.state.doc, selectFrom.value, selectTo.value);
          const transaction = editorView.state.tr.setSelection(newSelection);
          editorView.dispatch(transaction);

          const mark = editorView.state.schema.marks.fontsize.create({fontsize: item.value})
          editorView.dispatch(editorView.state.tr.addMark(selectFrom.value, selectTo.value, mark))
          updateListStyles(editorView, 'fontsize', mark);
          console.log(editorView.state.schema.nodes)
          // selectFrom.value = -1
        } else {
          const mark = editorView.state.schema.marks.fontsize.create({fontsize: item.value})
          if (autoSelectAll(editorView)) {
            showSelection()
          }
          addMark(editorView, mark)
          updateListStyles(editorView, 'fontsize', mark);
          console.log(editorView.state.schema.nodes)
        }
      }
    }
    else if (item.command === 'clearFontsize') {
      inFocus = false
      editorView.dispatch(editorView.state.tr.removeMark(0, editorView.state.doc.content.size, editorView.state.schema.marks.fontsize))
    }
    else if (item.command === 'clearTextStyle') {
      inFocus = false
      if (item.value === 'bold') {
        editorView.dispatch(editorView.state.tr.removeMark(0, editorView.state.doc.content.size, editorView.state.schema.marks.strong))
      } else if (item.value === 'italic') {
        editorView.dispatch(editorView.state.tr.removeMark(0, editorView.state.doc.content.size, editorView.state.schema.marks.em))
      } else if (item.value === 'underline') {
        editorView.dispatch(editorView.state.tr.removeMark(0, editorView.state.doc.content.size, editorView.state.schema.marks.underline))
      }

    }
    else if (item.command === 'fontsize-add') {
      const step = item.value ? +item.value : 2
      if (autoSelectAll(editorView)) {
        showSelection()
      }
      const fontsize = getFontsize(editorView) + step + 'px'
      const mark = editorView.state.schema.marks.fontsize.create({ fontsize })
      addMark(editorView, mark)
    }
    else if (item.command === 'fontsize-reduce') {
      const step = item.value ? +item.value : 2
      if (autoSelectAll(editorView)) {
        showSelection()
      }
      let fontsize = getFontsize(editorView) - step
      if (fontsize < 12) fontsize = 12
      const mark = editorView.state.schema.marks.fontsize.create({ fontsize: fontsize + 'px' })
      addMark(editorView, mark)
    }
    else if (item.command === 'color' && item.value) {
      if (selectedWidget.value.prototypeTile) {
        const allText = allTextSelected()
        if (allText) {
          savePrototypeTileStyle(item)
        }
      } else {
        if (autoSelectAll(editorView)) {
          showSelection()
        }
        const mark = editorView.state.schema.marks.forecolor.create({ color: item.value })
        addMark(editorView, mark)
        updateListStyles(editorView, 'forecolor', mark);
      }
    }
    else if (item.command === 'backcolor' && item.value) {
      const mark = editorView.state.schema.marks.backcolor.create({ backcolor: item.value })
      if (autoSelectAll(editorView)) {
        showSelection()
      }
      addMark(editorView, mark)
    }
    else if (item.command === 'bold') {
      if (selectedWidget.value.prototypeTile) {
        const allText = allTextSelected()
        if (allText) {
          savePrototypeTileStyle(item)
        }
      } else {
        if (autoSelectAll(editorView)) {
          showSelection()
        }
        toggleMark(editorView.state.schema.marks.strong)(editorView.state, editorView.dispatch)
      }
    }
    else if (item.command === 'em') {
      if (selectedWidget.value.prototypeTile) {
        const allText = allTextSelected()
        if (allText) {
          savePrototypeTileStyle(item)
        }
      } else {
        if (autoSelectAll(editorView)) {
          showSelection()
        }
        toggleMark(editorView.state.schema.marks.em)(editorView.state, editorView.dispatch)
      }
    }
    else if (item.command === 'underline') {
      if (selectedWidget.value.prototypeTile) {
        const allText = allTextSelected()
        if (allText) {
          savePrototypeTileStyle(item)
        }
      } else {
        if (autoSelectAll(editorView)) {
          showSelection()
        }
        toggleMark(editorView.state.schema.marks.underline)(editorView.state, editorView.dispatch)
      }
    }
    else if (item.command === 'strikethrough') {
      if (autoSelectAll(editorView)) {
        showSelection()
      }
      toggleMark(editorView.state.schema.marks.strikethrough)(editorView.state, editorView.dispatch)
    }
    else if (item.command === 'subscript') {
      toggleMark(editorView.state.schema.marks.subscript)(editorView.state, editorView.dispatch)
    }
    else if (item.command === 'superscript') {
      toggleMark(editorView.state.schema.marks.superscript)(editorView.state, editorView.dispatch)
    }
    else if (item.command === 'blockquote') {
      wrapIn(editorView.state.schema.nodes.blockquote)(editorView.state, editorView.dispatch)
    }
    else if (item.command === 'code') {
      toggleMark(editorView.state.schema.marks.code)(editorView.state, editorView.dispatch)
    }
    else if (item.command === 'align' && item.value) {
      alignmentCommand(editorView, item.value)
    }
    else if (item.command === 'indent' && item.value) {
      indentCommand(editorView, +item.value)
    }
    else if (item.command === 'bulletList') {
      const listStyleType = item.value || ''

      const { bullet_list: bulletList, list_item: listItem } = editorView.state.schema.nodes
      toggleList(bulletList, listItem, listStyleType)(editorView.state, editorView.dispatch)

    }
    else if (item.command === 'orderedList') {
      const listStyleType = item.value || ''
      const { ordered_list: orderedList, list_item: listItem } = editorView.state.schema.nodes
      toggleList(orderedList, listItem, listStyleType)(editorView.state, editorView.dispatch)

    }
    else if (item.command === 'clear') {
      if (autoSelectAll(editorView)) {
        showSelection()
      }
      const { $from, $to } = editorView.state.selection
      editorView.dispatch(editorView.state.tr.removeMark($from.pos, $to.pos))
    }
    else if (item.command === 'link') {
      const markType = editorView.state.schema.marks.link
      const { from, to } = editorView.state.selection
      const result = findNodesWithSameMark(editorView.state.doc, from, to, markType)
      if (result) {
        if (item.value) {
          const mark = editorView.state.schema.marks.link.create({ href: item.value, title: item.value })
          addMark(editorView, mark, { from: result.from.pos, to: result.to.pos + 1 })
        }
        else editorView.dispatch(editorView.state.tr.removeMark(result.from.pos, result.to.pos + 1, markType))
      }
      else if (markActive(editorView.state, markType)) {
        if (item.value) {
          const mark = editorView.state.schema.marks.link.create({ href: item.value, title: item.value })
          addMark(editorView, mark)
        }
        else toggleMark(markType)(editorView.state, editorView.dispatch)
      }
      else if (item.value) {
        if (autoSelectAll(editorView)) {
          showSelection()
        }
        toggleMark(markType, { href: item.value, title: item.value })(editorView.state, editorView.dispatch)
      }
    }
    else if (item.command === 'insert' && item.value) {
      editorView.dispatch(editorView.state.tr.insertText(item.value))
    }
  }

  if (inFocus) editorView.focus()
  handleInput()
  handleClick()
}

const onTextSelected = (anchorNode, selectionType) => {
  setTimeout(() => {
    if (anchorNode) {
      const fontSize = getFontsize(editorView);
      const align = getAlignment(editorView);
      const bold = getBold(editorView)
      const italic = getItalic(editorView)
      const underline = getUnderline(editorView)
      const color = getColor(editorView)
      const bulletList = getListState(editorView)

      fontSize && (selectionFontSize.value = fontSize)
      color && (selectionColor.value = color)
      selectionBold.value = bold
      selectionItalic.value = italic
      selectionUnderline.value = underline
      align && (alignment.value = align)
      selectionList.value = bulletList

      textSelected.value = true

    }
  }, 100)

}

const updateListStyles = (editorView, type, mark) => {

  let isInBulletList = false;

  editorView.state.doc.nodesBetween(selectFrom.value, selectTo.value, (node, pos) => {
    if (node.type.name === 'bullet_list' || node.type.name === 'list_item') {
      isInBulletList = true;
    }
  });

  if (isInBulletList) {
    const tr = editorView.state.tr;

    editorView.state.doc.nodesBetween(selectFrom.value, selectTo.value, (node, pos) => {
      if (node.type.name === 'bullet_list') {
        const attrs = {
          ...node.attrs,
          ...(type === 'fontsize' ? { fontSize: mark.attrs.fontsize, marginLeft: `${parseInt(mark.attrs.fontsize) + 10}px` } : {}),
          ...(type === 'forecolor' ? { color : mark.attrs.color } : {})
        };
        tr.setNodeMarkup(pos, null, attrs);
      }
    });

    editorView.dispatch(tr);

  } else {
   return ''
  }
};

const getAlignment = (editorView) => {

  const { from, to } = editorView.state.selection;
  let alignment = 'left'; // Default alignment

  editorView.state.doc.nodesBetween(from, to, (node) => {
    if (node.attrs && node.attrs.align) {
      alignment = node.attrs.align;
    }
  });

  return alignment;
};

// When the mouse is lifted, execute the format brush command
const handleMouseup = () => {
  if (!textFormatPainter.value) return

  const actions: RichTextAction[] = [{ command: 'clear' }]
  for (const key of Object.keys(textFormatPainter.value)) {
    const command = key
    const value = textFormatPainter.value[key]
    if (value) actions.push({ command, value })
  }
  execCommand({ action: actions })
  mainStore.setTextFormatPainter(null)
}

// Initialization and uninstallation of the Prosemirror editor
onMounted(() => {
  emitter.on(EmitterEvents.RICH_TEXT_COMMAND, execCommand)

  editorView = initProsemirrorEditor((editorViewRef.value as Element), textContent.value, {
    handleDOMEvents: {
      focus: handleFocus,
      blur: handleBlur,
      keydown: handleKeydown,
      click: handleClick,
      mouseup: handleMouseup,
      dragover: (view, event) => {
        event.preventDefault();
        event.stopPropagation();
        return false;
      },
      drop: (view, event) => {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    },
    editable: () => props.editable,
  })
  if (props.autoFocus) editorView.focus()

  const existingHandler = document.onselectionchange;
  document.onselectionchange = () => {
    if (existingHandler) {
      existingHandler();
    }
    handleSelectionChange();
  };

  // removing Arial fontname from individual text places
  editorView.dispatch(editorView.state.tr.removeMark(0, editorView.state.doc.content.size, editorView.state.schema.marks.fontname))
})
onUnmounted(() => {
  editorView && editorView.destroy()
})


onUnmounted(() => {
  emitter.off(EmitterEvents.RICH_TEXT_COMMAND, execCommand)
})
</script>

<style lang="scss" scoped>
.prosemirror-editor {
  cursor: text;
  caret-color: black;
  overflow-x: hidden;

  &.format-painter {
    cursor: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABkAAAAVCAYAAACzK0UYAAAABHNCSVQICAgIfAhkiAAAAVRJREFUSInt1DFuwjAYBeCXUrFavgBN9yB6AKR6Bi7AVLrlBpFYgAUpp2i37AysVDIXcCIuwJRMEEYk9LrQDlVQ7EiVOvSt/v1/tmUbeZ7TGMPL5WLgEJLzNE2ptabWmsfjkTeLjTGUUvJ8Pjsjo9GIUkpKKam1voncuTRumn/EKfd1BSQnAF4qhvyK2k1VD88YQ6UUiqJI2+12r2LiPI7j2Xa7rV9yRZbLpRWiAKhGwjW1x3XN828jD9PpVK3X60bAarWy20lZltjv940QwO4KPzbu7oCgLMu/g3Q6ncZI73Q6WSFhGDZGnrIss0LG4zGEEG4ISZUkiW8DDAYDCCEQBIEbAmAWx7GNgSiKAOB1OBzaIyQnSZIom/cRRRG63e7C87z3MAw/fu7Gy/OcRVEgCIK01Wp9/10k37Ism9TdLCHEFzC/zvMPh8Nmt9v5ANDv9/EJD8ykxYswZDkAAAAASUVORK5CYII=) 1 10, default !important;
  }

  :deep(.prose-selection) {
    background-color: rgba(#5B9BD5, 0.3);
    color: inherit;
  }
}

.no-drag {
  -webkit-user-drag: none; /* Safari */
  -moz-user-drag: none; /* Firefox */
  -ms-user-drag: none; /* Internet Explorer/Edge */
  user-drag: none; /* Standard property */
}

</style>
